<script setup lang="ts">
import type { BarometerCar } from '@autobid/ui/types/Car'
import type { BidOptionsEvent } from '@autobid/ui/types/websocket/BidOptionsEvent'
import { useWebSocketStore } from '@autobid/nuxt-pinia-store/store/useWebSocketStore'
import CommonDialog from '@autobid/ui/components/common/dialog/Dialog.vue'
import {
  bidValidation,
  getMinimalBidStep,
} from '@autobid/ui/utils/car/bidValidation'
import { getMinimalInputPrice } from '@autobid/ui/utils/car/getMinimalInputPrice'
import { formatPrice } from '@autobid/ui/utils/formatPrice'
import { onMounted, onUnmounted } from 'vue'

interface Props {
  bid: number | null
  isOpen: boolean
  type: 1 | 2
  isBidding: boolean
  car: BarometerCar
}

const props = defineProps<Props>()

const emits = defineEmits(['close', 'confirm'])

const { startListen, endListen } = useWebSocketStore()

const { t } = useI18n()
const push = usePush()

function checkIfCanBid(event: BidOptionsEvent) {
  if (!props.isOpen || !props.bid) {
    return
  }
  // update car manually to prevent accessing props car with stale options
  const updatedCar = { ...props.car, bidOptions: event.context.options }
  const minBidStep = getMinimalBidStep(updatedCar.bidOptions)
  const minPrice = getMinimalInputPrice(updatedCar, minBidStep)
  const isBidStepValid = bidValidation(props.bid, updatedCar)
  const isMinPriceValid = props.bid >= minPrice

  if (isBidStepValid && isMinPriceValid)
    return

  push.warning(t('barometer.bid-options-changed-message'))
  emits('close')
}

const CALLBACK_NAME = 'check-if-can-bid-confirm'
onMounted(() => {
  startListen({
    eventName: 'premium.BID_OPTIONS',
    callbackName: CALLBACK_NAME,
    callback: checkIfCanBid,
  })
})

onUnmounted(() => {
  endListen({ eventName: 'premium.BID_OPTIONS', callbackName: CALLBACK_NAME })
})
</script>

<template>
  <CommonDialog
    disable-dismiss-on-click-outside
    :opened="isOpen"
    @close="$emit('close')"
  >
    <template #title>
      {{
        type === 1
          ? $t('bid-confirmation-modal.title')
          : $t('bid-confirmation-modal.title-2')
      }}
    </template>
    <template #content>
      <div class="flex w-full flex-col gap-2">
        <h2 v-if="bid" class="text-2xl">
          {{
            $t(
              type === 1
                ? 'bid-confirmation-modal.question'
                : 'bid-confirmation-modal.question-2',
              {
                bid: formatPrice(bid),
              },
            )
          }}
        </h2>
        <p class="text-sm text-gray-500">
          {{
            type === 1
              ? $t('bid-confirmation-modal.description')
              : $t('bid-confirmation-modal.description-2')
          }}
        </p>
      </div>
    </template>
    <template #buttons>
      <footer class="flex w-full justify-end gap-2">
        <ElementsButton class="flex-1 bg-red" @click="$emit('close')">
          {{
            $t('bid-confirmation-modal.cancel')
          }}
        </ElementsButton>
        <ElementsButton
          class="ml-auto flex-1"
          :disabled="isBidding"
          @click="$emit('confirm')"
        >
          {{
            isBidding
              ? $t('bid-confirmation-modal.loading')
              : $t('bid-confirmation-modal.confirm')
          }}
        </ElementsButton>
      </footer>
    </template>
  </CommonDialog>
</template>
